import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { selectShiftsByAssignment } from '../../redux/shifts/shiftsSelectors';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { selectPeriod, selectSelectedShift, selectShiftStatus, setSelectedShift } from '../../redux/shifts/shiftsSlice';
import { calculateWeekDays } from '../../utils/dateUtils';
import Spinner from '../../assets/svg/spinner';
import { AssignedShifts, ScheduleToolbar, UnassignedShifts, TableHeader } from './components/index';
import { DRAWER_TYPES } from '../../pages/AdminShiftView/adminShiftDrawerConfig';

import {
  SpinnerContainer,
  StyledContainer,
  StyledContent,
  StyledTableBody,
  StyledTable,
  EmptyStateContainer,
  StyledTableCellEmpty,
  StyledTableRow,
} from './AdminShiftTable.styles';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AdminShiftTable = ({ onOpenDrawer, isDrawerOpen = false }) => {
  const dispatch = useDispatch();
  //SELECTORS
  const shiftsByAssignment = useSelector(selectShiftsByAssignment);
  const shiftsStatus = useSelector(selectShiftStatus);
  const { startOfWeekIndex } = useSelector(selectCurrentVenue);
  const selectedShift = useSelector(selectSelectedShift);
  const { start } = useSelector(selectPeriod);

  //STATES
  const [dateFromShiftButton, setDateFromShiftButton] = useState(null);
  const [selectedDate, setSelectedDate] = useState(start ? dayjs(start).toDate() : new Date());

  // IMPORTANT This useEffect is to update the selected date when the period changes
  useEffect(() => {
    if (start) {
      setSelectedDate(dayjs(start).toDate());
    }
  }, [start]);

  //VARIABLES
  const isLoadingShifts = shiftsStatus === 'loading';
  const daysOfWeek = useMemo(() => calculateWeekDays(selectedDate, startOfWeekIndex), [selectedDate, startOfWeekIndex]);
  const assignedShifts = useMemo(() => Object.values(shiftsByAssignment.assigned).reduce((memo, _) => memo.concat(_), []), [shiftsByAssignment.assigned]);
  const hasUnassignedShifts = shiftsByAssignment.unassigned.length > 0;
  const hasAssignedShifts = Object.keys(shiftsByAssignment.assigned || {}).length > 0;
  const hasShifts = hasUnassignedShifts || hasAssignedShifts;

  const countByDay = [].concat(shiftsByAssignment.unassigned, assignedShifts).reduce((memo, _) => {
    const dayOfWeek = DAY_NAMES[new Date(_.start).getDay()];
    memo[dayOfWeek] = memo[dayOfWeek] || 0;
    memo[dayOfWeek] += 1;
    return memo;
  }, {});

  const handleOpenShiftDrawer = (shift, isAssigned) => {
    dispatch(setSelectedShift(shift));
    onOpenDrawer(DRAWER_TYPES.SHIFT_INFORMATION, { shift, isAssigned });
  };

  const handleOpenAddShiftDrawer = (data = {}) => {
    onOpenDrawer(DRAWER_TYPES.ADD_SHIFT, {
      selectedDate: data.selectedDate || selectedDate,
      dateFromShiftButton: data.dateFromShiftButton || dateFromShiftButton,
    });
  };

  const handleDateFromShiftButtonChange = (newDate) => {
    setDateFromShiftButton(newDate);
  };


  return (
    <StyledContainer>
      <StyledContent className={isDrawerOpen ? 'drawer-open' : ''}>
        <ScheduleToolbar
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onAddShift={() => handleOpenAddShiftDrawer({ selectedDate })}
        />
        {isLoadingShifts ? (
          <StyledTable>
            <StyledTableBody>
              <StyledTableRow>
                <StyledTableCellEmpty colSpan={daysOfWeek.length + 1}>
                  <SpinnerContainer>
                    <Spinner />
                  </SpinnerContainer>
                </StyledTableCellEmpty>
              </StyledTableRow>
            </StyledTableBody>
          </StyledTable>
        ) : (
          <StyledTable>
            <TableHeader
              daysOfWeek={
                daysOfWeek.map(_ => ({ ..._, count: countByDay[_.day] || 0 }))
              }
              unassignedShiftsCount={
                hasUnassignedShifts ? Object.values(shiftsByAssignment.unassigned).flat().length : 0
              }
            />
            {hasShifts ? (
              <StyledTableBody>
                {hasUnassignedShifts && (
                  <UnassignedShifts
                    shifts={shiftsByAssignment.unassigned}
                    daysOfWeek={daysOfWeek}
                    startOfWeek={start ? dayjs(start).toDate() : null}
                    endOfWeek={start ? dayjs(start).add(6, 'day').endOf('day').toDate() : null}
                    selectedDate={selectedDate}
                    selectedShift={selectedShift}
                    onShiftClick={(shift) => handleOpenShiftDrawer(shift, false)}
                    onAddShift={() => handleOpenAddShiftDrawer({ selectedDate, dateFromShiftButton })}
                    dateFromShiftButton={dateFromShiftButton}
                    onDateFromShiftButtonChange={handleDateFromShiftButtonChange}
                  />
                )}
                {hasAssignedShifts && (
                  <AssignedShifts
                    shifts={shiftsByAssignment.assigned}
                    daysOfWeek={daysOfWeek}
                    onShiftClick={(shift) => handleOpenShiftDrawer(shift, true)}
                  />
                )}
              </StyledTableBody>
            ) : (
              <StyledTableBody>
                <StyledTableRow>
                  <StyledTableCellEmpty colSpan={daysOfWeek.length + 1}>
                    <EmptyStateContainer>
                      <Typography variant="h5">No shifts scheduled for this week.</Typography>
                    </EmptyStateContainer>
                  </StyledTableCellEmpty>
                </StyledTableRow>
              </StyledTableBody>
            )}
          </StyledTable>
        )}
      </StyledContent>
    </StyledContainer>
  );
};

AdminShiftTable.propTypes = {
  onOpenDrawer: PropTypes.func.isRequired,
  isDrawerOpen: PropTypes.bool,
};

export default AdminShiftTable;
